if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    // Minimum pixel difference required between the zoom image width and the product image width
    // for zoom functionality to be enabled.
    static ZOOM_IMAGE_OFFSET = 75;
    static ZOOM_SCALE_FACTOR = 1.5;
    static PAN_STEP = 10;

    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      // Clean up zoom event listeners
      if (this.zoomLinks) {
        this.zoomLinks.forEach((el) => {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.removeEventListener('touchstart', this.touchStartListener);
          el.removeEventListener('touchmove', this.touchMoveListener);
          el.removeEventListener('touchend', this.touchEndListener);
          el.removeEventListener('keydown', this.zoomKeyDownListener);
          el.removeEventListener('blur', this.zoomBlurListener);
        });
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled') && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        this.touchStartListener = this.touchStartListener || this.handleTouchStart.bind(this);
        this.touchMoveListener = this.touchMoveListener || this.handleTouchMove.bind(this);
        this.touchEndListener = this.touchEndListener || this.handleTouchEnd.bind(this);
        this.zoomKeyDownListener = this.zoomKeyDownListener || this.handleZoomKeyDown.bind(this);
        this.zoomBlurListener = this.zoomBlurListener || this.handleZoomBlur.bind(this);
        this.keyboardZoomState = null;
        this.keyboardZoomPosition = { x: 50, y: 50 };
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener(
            'click',
            (evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            },
            { passive: false },
          );
          el.addEventListener('keydown', this.zoomKeyDownListener);
          el.addEventListener('blur', this.zoomBlurListener);
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(`[data-media-id="${evt.detail.variant.featured_media.id}"]`);
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {},
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: [],
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`,
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName ? mediaGroupMap.groups[groupName] : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(`[data-media-id="${this.currentItem.dataset.mediaId}"]`);
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(
            () => {
              img.loading = 'eager';
            },
            500 * (index + 1),
          );
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) {
        this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
        this.thumbs.addEventListener('focusin', this.handleThumbFocus.bind(this));
      }
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomImage = el.querySelector('.zoom-image');
        const productImage = el.querySelector('.product-image');

        if (!zoomImage || !productImage) return;

        const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
        const imageWidth = productImage.getBoundingClientRect().width;

        // Enable zoom if zoom image is significantly larger
        if (zoomWidth - MediaGallery.ZOOM_IMAGE_OFFSET > imageWidth) {
          // Always enable both mouse and touch events for better accessibility
          // This supports desktop users who zoom in to mobile viewport sizes
          el.addEventListener('mousemove', this.zoomEventListener);
          el.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
          el.addEventListener('touchstart', this.touchStartListener, { passive: false });
          el.addEventListener('touchmove', this.touchMoveListener, { passive: false });
          el.addEventListener('touchend', this.touchEndListener, { passive: false });
          el.classList.remove('pointer-events-none');
        } else {
          if (this.keyboardZoomState === el) {
            this.exitKeyboardZoom(el);
          }
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.removeEventListener('touchstart', this.touchStartListener);
          el.removeEventListener('touchmove', this.touchMoveListener);
          el.removeEventListener('touchend', this.touchEndListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Force zoom image to be visible (same as touch behavior)
      zoomImage.style.display = 'block';
      zoomImage.style.opacity = '1';
      zoomImage.style.position = 'absolute';
      zoomImage.style.top = '0';
      zoomImage.style.left = '0';
      zoomImage.style.width = '100%';
      zoomImage.style.height = '100%';
      zoomImage.style.zIndex = '10';
      zoomImage.style.objectFit = 'none';
      zoomImage.style.transform = `scale(${MediaGallery.ZOOM_SCALE_FACTOR})`;
      zoomImage.style.transformOrigin = '50% 50%';

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        // Use offsetX/offsetY for mouse events, pageX/pageY for touch events
        const offsetX = evt.offsetX !== undefined ? evt.offsetX : evt.pageX - hoverElem.getBoundingClientRect().left;
        const offsetY = evt.offsetY !== undefined ? evt.offsetY : evt.pageY - hoverElem.getBoundingClientRect().top;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        console.error('Error updating zoom position:', err);
        zoomImage.style.objectPosition = '50% 50%'; // Fallback to center position
      }
    }

    /**
     * Handles mouse leave to hide zoom
     * @param {MouseEvent} evt - Mouse event object
     */
    handleMouseLeave(evt) {
      const hoverElem = evt.currentTarget;

      // Don't hide zoom if keyboard zoom is active on this element
      if (this.keyboardZoomState === hoverElem) return;

      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Hide zoom image
      zoomImage.style.display = '';
      zoomImage.style.opacity = '';
      zoomImage.style.transform = '';
    }

    /**
     * Handles touch start for mobile zoom
     * @param {TouchEvent} evt - Touch event object
     */
    handleTouchStart(evt) {
      evt.preventDefault();

      // Debug: Log touch attempts

      // Prevent double initialization
      if (this.isTouching) return;

      const touchElem = evt.currentTarget;
      const zoomImage = touchElem.querySelector('.js-zoom-image');

      if (!zoomImage) {
        console.error('Zoom image not found');
        return;
      }

      this.isTouching = true;

      // Show zoom image with inline styles
      zoomImage.style.display = 'block';
      zoomImage.style.opacity = '1';
      zoomImage.style.position = 'absolute';
      zoomImage.style.top = '0';
      zoomImage.style.left = '0';
      zoomImage.style.width = '100%';
      zoomImage.style.height = '100%';
      zoomImage.style.zIndex = '10';
      zoomImage.style.objectFit = 'none';
      zoomImage.style.objectPosition = '50% 50%';
      zoomImage.style.transform = `scale(${MediaGallery.ZOOM_SCALE_FACTOR})`;
      zoomImage.style.transformOrigin = '50% 50%';

      // Add hover class to show zoom
      touchElem.classList.add('media--hover');
      touchElem.classList.add('is-touching');
    }

    /**
     * Handles touch move for mobile zoom
     * @param {TouchEvent} evt - Touch event object
     */
    handleTouchMove(evt) {
      if (!this.isTouching) return;
      evt.preventDefault();

      const touch = evt.touches[0];
      if (touch) {
        const touchElem = evt.currentTarget;
        const zoomImage = touchElem.querySelector('.js-zoom-image');
        const productImage = touchElem.querySelector('.product-image');

        // Download the zoom image if necessary
        if (zoomImage.dataset.src) {
          if (this.loadingSpinner) this.loadingSpinner.classList.remove('loading-spinner--out');

          const img = new Image();
          img.src = zoomImage.dataset.src;
          img.onload = () => {
            zoomImage.src = img.src;
            touchElem.classList.remove('media--zoom-not-loaded');
            if (this.loadingSpinner) this.loadingSpinner.classList.add('loading-spinner--out');
          };
          zoomImage.removeAttribute('data-src');
        }

        // Update zoom position
        try {
          const rect = productImage.getBoundingClientRect();
          const offsetX = touch.clientX - rect.left;
          const offsetY = touch.clientY - rect.top;
          const x = (offsetX / rect.width) * 100;
          const y = (offsetY / rect.height) * 100;

          zoomImage.style.objectPosition = `${x}% ${y}%`;
        } catch (err) {
          console.error('Error updating zoom position:', err);
          zoomImage.style.objectPosition = '50% 50%'; // Fallback to center position
        }
      }
    }

    /**
     * Handles touch end for mobile zoom
     * @param {TouchEvent} evt - Touch event object
     */
    handleTouchEnd(evt) {
      evt.preventDefault();

      // Reset touch state immediately
      this.isTouching = false;

      const touchElem = evt.currentTarget;

      // Don't hide zoom if keyboard zoom is active on this element
      if (this.keyboardZoomState === touchElem) return;

      const zoomImage = touchElem.querySelector('.js-zoom-image');

      // Hide zoom image
      zoomImage.style.display = '';
      zoomImage.style.opacity = '';
      zoomImage.style.transform = '';

      // Remove classes
      touchElem.classList.remove('media--hover');
      touchElem.classList.remove('is-touching');
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow =
        evt.target === this.nextBtn
          ? this.visibleItems[this.currentIndex + 1]
          : this.visibleItems[this.currentIndex - 1];

      // Queue focus move for keyboard users (evt.detail === 0 means keyboard activation)
      if (evt.detail === 0) {
        this.pendingFocusSlide = itemToShow;
      }

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles 'focusin' events on the thumbnails container.
     * Updates the main image when a thumbnail receives keyboard focus.
     * @param {FocusEvent} evt - Focus event object.
     */
    handleThumbFocus(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`.media-viewer__item[data-media-id="${thumb.dataset.mediaId}"]`);
      if (itemToShow) {
        this.customSetActiveMedia(itemToShow);
      }
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;

      // Exit keyboard zoom if active when slide changes
      if (this.keyboardZoomState) {
        this.exitKeyboardZoom(this.keyboardZoomState);
      }

      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth',
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      // Hide non-current slides from screen readers and keyboard focus
      if (this.viewerItems) {
        this.viewerItems.forEach((item) => {
          item.inert = true;
        });
        mediaItem.inert = false;
      }

      // Move focus to zoom link after keyboard nav button activation
      if (this.pendingFocusSlide === mediaItem) {
        const zoomLink = mediaItem.querySelector('.js-zoom-link');
        if (zoomLink) zoomLink.focus();
        this.pendingFocusSlide = null;
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(`[data-media-id="${this.currentItem.dataset.mediaId}"]`);
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
      this.updateSlideLabels();
    }

    /**
     * Updates aria-labels on slide items to reflect current visible positions.
     */
    updateSlideLabels() {
      const total = this.visibleItems.length;
      this.visibleItems.forEach((item, index) => {
        item.setAttribute(
          'aria-label',
          theme.strings.slideOf.replace('[current]', index + 1).replace('[total]', total),
        );
      });
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Handles keydown events on zoom links for keyboard-accessible zoom.
     * @param {KeyboardEvent} evt - Keyboard event object.
     */
    handleZoomKeyDown(evt) {
      const zoomLink = evt.currentTarget;

      // Skip if zoom is disabled on this link
      if (zoomLink.classList.contains('pointer-events-none')) return;

      switch (evt.key) {
        case 'Enter':
        case ' ':
          evt.preventDefault();
          if (this.keyboardZoomState === zoomLink) {
            this.exitKeyboardZoom(zoomLink);
          } else {
            this.enterKeyboardZoom(zoomLink);
          }
          break;

        case 'Escape':
          if (this.keyboardZoomState === zoomLink) {
            evt.preventDefault();
            this.exitKeyboardZoom(zoomLink);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          if (this.keyboardZoomState === zoomLink) {
            evt.preventDefault();
            this.panKeyboardZoom(evt.key);
          }
          break;
      }
    }

    /**
     * Handles blur events on zoom links — exits keyboard zoom when focus leaves.
     * @param {FocusEvent} evt - Focus event object.
     */
    handleZoomBlur(evt) {
      const zoomLink = evt.currentTarget;
      if (this.keyboardZoomState === zoomLink) {
        this.exitKeyboardZoom(zoomLink);
      }
    }

    /**
     * Enters keyboard zoom mode on a zoom link.
     * @param {Element} zoomLink - The zoom link element to zoom into.
     */
    enterKeyboardZoom(zoomLink) {
      const zoomImage = zoomLink.querySelector('.js-zoom-image');
      if (!zoomImage) return;

      this.keyboardZoomState = zoomLink;
      this.keyboardZoomPosition = { x: 50, y: 50 };

      // Load zoom image if needed (same pattern as mouse/touch)
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          zoomLink.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      // Show zoom image with inline styles (same pattern as mouse/touch)
      zoomImage.style.display = 'block';
      zoomImage.style.opacity = '1';
      zoomImage.style.position = 'absolute';
      zoomImage.style.top = '0';
      zoomImage.style.left = '0';
      zoomImage.style.width = '100%';
      zoomImage.style.height = '100%';
      zoomImage.style.zIndex = '10';
      zoomImage.style.objectFit = 'none';
      zoomImage.style.objectPosition = '50% 50%';
      zoomImage.style.transform = `scale(${MediaGallery.ZOOM_SCALE_FACTOR})`;
      zoomImage.style.transformOrigin = '50% 50%';

      // Hide the product image
      const productImage = zoomLink.querySelector('.product-image');
      if (productImage) productImage.style.opacity = '0';

      this.announceZoomState(true);
    }

    /**
     * Exits keyboard zoom mode on a zoom link.
     * @param {Element} zoomLink - The zoom link element to exit zoom on.
     */
    exitKeyboardZoom(zoomLink) {
      const zoomImage = zoomLink.querySelector('.js-zoom-image');
      if (!zoomImage) return;

      this.keyboardZoomState = null;

      // Clear zoom styles
      zoomImage.style.display = '';
      zoomImage.style.opacity = '';
      zoomImage.style.transform = '';

      // Restore product image
      const productImage = zoomLink.querySelector('.product-image');
      if (productImage) productImage.style.opacity = '';

      this.announceZoomState(false);
    }

    /**
     * Pans the keyboard zoom position by one step.
     * @param {string} key - The arrow key pressed.
     */
    panKeyboardZoom(key) {
      const step = MediaGallery.PAN_STEP;

      switch (key) {
        case 'ArrowUp':
          this.keyboardZoomPosition.y = Math.max(0, this.keyboardZoomPosition.y - step);
          break;
        case 'ArrowDown':
          this.keyboardZoomPosition.y = Math.min(100, this.keyboardZoomPosition.y + step);
          break;
        case 'ArrowLeft':
          this.keyboardZoomPosition.x = Math.max(0, this.keyboardZoomPosition.x - step);
          break;
        case 'ArrowRight':
          this.keyboardZoomPosition.x = Math.min(100, this.keyboardZoomPosition.x + step);
          break;
      }

      const zoomImage = this.keyboardZoomState.querySelector('.js-zoom-image');
      if (zoomImage) {
        zoomImage.style.objectPosition = `${this.keyboardZoomPosition.x}% ${this.keyboardZoomPosition.y}%`;
      }
    }

    /**
     * Announces the zoom state change to screen readers via the live region.
     * @param {boolean} isZoomed - Whether zoom is now active.
     */
    announceZoomState(isZoomed) {
      if (!this.liveRegion) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = isZoomed ? theme.strings.zoomIn : theme.strings.zoomOut;

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
